import {
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Headset,
  LoaderCircle,
  PhoneCall,
  PhoneForwarded,
  PhoneMissed,
  Undo2,
  Voicemail,
  type LucideIcon,
} from 'lucide-react'

import type {
  ConversationFacts,
  HandoffSummary,
  InteractionIntent,
} from './conversations-contracts'
import type { CallEvent } from './interaction-calls-contracts'

export type HandoffTone =
  'success' | 'progress' | 'warning' | 'danger' | 'neutral'

export type HandoffPresentation = {
  label: string
  description: string
  tone: HandoffTone
  icon: LucideIcon
}

// A transfer whose result never arrived reads as released rather than as still
// happening: the caller's leg ends within minutes, so its record was lost.
const IN_PROGRESS_WINDOW_MS = 30 * 60 * 1000

const UNAVAILABLE_REASONS: Partial<
  Record<NonNullable<HandoffSummary['reason']>, string>
> = {
  BREAK: 'The front desk was on break, so no transfer was attempted.',
  CLOSED: 'The clinic was closed, so no transfer was attempted.',
  DISABLED: 'Transfers are turned off for this clinic.',
  HOURS_UNVERIFIED:
    'Clinic hours couldn’t be verified, so no transfer was attempted.',
  NOT_CONFIRMED:
    'The caller’s request couldn’t be confirmed, so no transfer was attempted.',
  NOT_ANSWERED: 'The front desk had already not answered on this call.',
  OTHER: 'The front desk couldn’t be reached, so no transfer was attempted.',
}

const NOT_CONNECTED_REASONS: Partial<
  Record<NonNullable<HandoffSummary['reason']>, string>
> = {
  NO_ANSWER: 'The front desk line didn’t answer.',
  BUSY: 'The front desk line was busy.',
  VOICEMAIL: 'The transfer reached voicemail.',
  FAILED: 'The transfer failed to connect.',
  CANCELLED: 'The transfer was cancelled.',
}

const CALLBACK_NOTES = {
  SAVED: 'Callback requested.',
  NOT_SAVED: 'A callback couldn’t be saved. Follow up with the caller.',
  PENDING: 'The callback request is still pending.',
} as const

function withCallback(description: string, handoff: HandoffSummary) {
  return handoff.callback === 'SAVED' ||
    handoff.callback === 'NOT_SAVED' ||
    handoff.callback === 'PENDING'
    ? `${description} ${CALLBACK_NOTES[handoff.callback]}`
    : description
}

function stateCopy(
  handoff: HandoffSummary,
  atMs: number,
  nowMs: number
): Omit<HandoffPresentation, 'icon'> & { icon: LucideIcon } {
  switch (handoff.state) {
    case 'ANSWERED':
      return {
        label: 'Front Desk Answered',
        description: 'The transfer connected and the front desk answered.',
        tone: 'success',
        icon: CircleCheck,
      }
    case 'TRANSFERRED':
      return {
        label: 'Transferred to Front Desk',
        description: 'The transfer went through. Staff pickup isn’t verified.',
        tone: 'neutral',
        icon: PhoneForwarded,
      }
    case 'IN_PROGRESS':
      return nowMs - atMs > IN_PROGRESS_WINDOW_MS
        ? {
            label: 'Transferred to Front Desk',
            description: 'The final result of this transfer wasn’t recorded.',
            tone: 'neutral',
            icon: PhoneForwarded,
          }
        : {
            label: 'Transfer in Progress',
            description: 'The result of this transfer isn’t recorded yet.',
            tone: 'progress',
            icon: LoaderCircle,
          }
    case 'RETURNED':
      return {
        label: 'Front Desk Unavailable — Returned to Ava',
        description:
          'The front desk didn’t answer, so the call returned to Ava.',
        tone: 'warning',
        icon: Undo2,
      }
    case 'NOT_ANSWERED':
      return {
        label: 'Transfer Not Completed',
        description:
          'The front desk didn’t answer, and the caller didn’t reach Ava again.',
        tone: 'danger',
        icon: PhoneMissed,
      }
    case 'CALLER_LEFT':
      return {
        label: 'Transfer Not Completed',
        description: 'The caller hung up before the transfer connected.',
        tone: 'warning',
        icon: PhoneMissed,
      }
    case 'NOT_CONNECTED':
      return {
        label: 'Transfer Not Completed',
        description:
          (handoff.reason && NOT_CONNECTED_REASONS[handoff.reason]) ??
          'The transfer didn’t connect.',
        tone: 'danger',
        icon: PhoneMissed,
      }
    case 'UNAVAILABLE':
      return {
        label: 'Front Desk Unavailable',
        description:
          (handoff.reason && UNAVAILABLE_REASONS[handoff.reason]) ??
          'The front desk couldn’t be reached.',
        tone: 'warning',
        icon: Headset,
      }
    case 'REQUESTED':
      return {
        label: 'Front Desk Requested',
        description:
          'The caller asked for the front desk. No transfer was made.',
        tone: 'neutral',
        icon: Headset,
      }
    default:
      return {
        label: 'Transfer Outcome Unverified',
        description: 'Flio’s records of this transfer are incomplete.',
        tone: 'neutral',
        icon: CircleHelp,
      }
  }
}

const TRANSFER_STATES = new Set<HandoffSummary['state']>([
  'IN_PROGRESS',
  'ANSWERED',
  'TRANSFERRED',
  'RETURNED',
  'NOT_ANSWERED',
  'CALLER_LEFT',
  'NOT_CONNECTED',
  'UNVERIFIED',
])

export type ConversationOutcome = Omit<HandoffPresentation, 'label'> & {
  label: string | null
}

/**
 * What the conversation came to, for a row, a call card or a final status. Always
 * beside the primary intent, never in place of it, and only what matters most:
 * - a question Ava settled says so, whatever happened to the desk on the way;
 * - a transfer that was attempted says how it ended;
 * - what the clinic now holds (a saved callback or message) comes next;
 * - a desk that couldn't take the call is not an outcome. No transfer was attempted,
 *   so that is only a note here, and an event in the timeline.
 * Null when nothing adds to the intent.
 */
export function conversationOutcome(
  facts: ConversationFacts,
  { atMs, nowMs = Date.now() }: { atMs: number; nowMs?: number }
): ConversationOutcome | null {
  const { intent, secondary = [], handoff, information, need } = facts
  const state = handoff?.state
  const callback = handoff?.callback
  if (
    intent === 'GENERAL_INQUIRY' &&
    information === 'ANSWERED' &&
    need === 'RESOLVED'
  ) {
    return {
      label: 'Information Provided',
      description:
        'Ava answered from clinic records, and the caller asked for nothing further.',
      tone: 'success',
      icon: CircleCheck,
    }
  }
  const messageLeft = secondary.includes('MESSAGE_LEFT')
  if (handoff && TRANSFER_STATES.has(state)) {
    const description = withCallback(
      stateCopy(handoff, atMs, nowMs).description,
      handoff
    )
    return {
      ...stateCopy(handoff, atMs, nowMs),
      description: messageLeft ? `${description} Message saved.` : description,
    }
  }
  // Once the caller's need is met, the desk that couldn't take them is history.
  const deskNote =
    handoff && state && need !== 'RESOLVED'
      ? stateCopy(handoff, atMs, nowMs).description
      : null
  if (callback === 'SAVED' && intent !== 'CALLBACK_REQUEST') {
    return {
      label: 'Callback Requested',
      description:
        deskNote ?? 'The clinic has a callback request for this caller.',
      tone: 'success',
      icon: PhoneCall,
    }
  }
  if (messageLeft) {
    return {
      label: 'Message Left',
      description: deskNote ?? 'The clinic has a message from this caller.',
      tone: 'success',
      icon: Voicemail,
    }
  }
  if (callback === 'PENDING') {
    return {
      label: 'Callback Pending',
      description: CALLBACK_NOTES.PENDING,
      tone: 'progress',
      icon: PhoneCall,
    }
  }
  if (callback === 'NOT_SAVED') {
    return {
      label: 'Callback Not Saved',
      description: CALLBACK_NOTES.NOT_SAVED,
      tone: 'danger',
      icon: CircleAlert,
    }
  }
  if (information === 'ANSWERED' && need === 'UNCONFIRMED') {
    return {
      label: 'Information Provided',
      description:
        'Ava answered from clinic records, but the call doesn’t show that settled the question.',
      tone: 'neutral',
      icon: CircleHelp,
    }
  }
  // A question that went unanswered says so. A request needs no such line: what
  // it came to is the intent itself, and the summary holds the rest.
  const openQuestion =
    need === 'UNRESOLVED' &&
    (intent === 'GENERAL_INQUIRY' || intent === 'APPOINTMENT_QUESTION')
  if (deskNote) {
    return {
      label: openQuestion ? 'Unresolved' : null,
      description: deskNote,
      tone: 'neutral',
      icon: Headset,
    }
  }
  if (information === 'NOT_AVAILABLE' && openQuestion) {
    return {
      label: 'Unresolved',
      description: 'Flio has no approved answer to this question.',
      tone: 'neutral',
      icon: CircleHelp,
    }
  }
  return null
}

const UNRESOLVED_NEEDS: Partial<Record<InteractionIntent, string>> = {
  CANCELLATION_REQUEST: 'The clinic still needs to handle this cancellation.',
  RESCHEDULE_REQUEST: 'The clinic still needs to handle this reschedule.',
  APPOINTMENT_REQUEST: 'This appointment request is still open.',
  APPOINTMENT_QUESTION: 'This question wasn’t answered on this call.',
  GENERAL_INQUIRY: 'This question wasn’t answered on this call.',
  FRONT_DESK_REQUEST: 'The caller didn’t reach the front desk.',
  CALLBACK_REQUEST: 'The clinic still needs to call this caller back.',
  MESSAGE_LEFT: 'The clinic still needs to act on this message.',
}

/** What is known of the need the caller came with, as the records support it. */
export function callerNeedCopy({ intent, need, handoff }: ConversationFacts): {
  label: string
  description: string
  tone: HandoffTone
} | null {
  if (!need) return null
  if (need === 'RESOLVED') {
    return {
      label: 'Resolved',
      tone: 'success',
      description:
        intent === 'APPOINTMENT_BOOKED'
          ? 'A verified booking met this request.'
          : intent === 'FRONT_DESK_REQUEST'
            ? 'The caller reached the front desk.'
            : 'Ava answered from clinic records, and the caller asked for nothing further.',
    }
  }
  if (need === 'UNCONFIRMED') {
    return {
      label: 'Not Confirmed',
      tone: 'neutral',
      description: TRANSFER_STATES.has(handoff?.state)
        ? 'Staff may have settled this on the transfer, but Flio has no record of it.'
        : 'Ava answered from clinic records, but the call doesn’t show that settled the question.',
    }
  }
  return {
    label: 'Unresolved',
    tone: 'neutral',
    description:
      (intent && UNRESOLVED_NEEDS[intent]) ??
      'This need wasn’t met on this call.',
  }
}

/** Attempted, completed and verified: three different things, kept apart. */
export function conversationAccount(
  {
    intent,
    secondary = [],
    handoff,
    information,
    requestPassed,
  }: ConversationFacts,
  atMs: number,
  nowMs: number = Date.now()
) {
  const state = handoff?.state
  const callback = handoff?.callback
  const transfer = TRANSFER_STATES.has(state)
  const attempted: string[] = []
  const completed: string[] = []
  const outcomes: string[] = []
  if (information) attempted.push('Looked up clinic information')
  if (state === 'UNAVAILABLE') {
    attempted.push('Checked whether the front desk could take the call')
  }
  if (transfer) attempted.push('Attempted a transfer to the front desk')
  if (callback === 'SAVED' || callback === 'PENDING') {
    attempted.push('Requested a callback for the caller')
  } else if (callback === 'NOT_SAVED') {
    attempted.push('Tried to save a callback')
  } else if (callback) {
    attempted.push('Asked the caller to confirm a callback')
  }

  if (intent === 'APPOINTMENT_BOOKED') completed.push('Booked the appointment')
  if (information === 'ANSWERED') {
    completed.push('Answered from approved clinic information')
  }
  if (requestPassed) completed.push('Passed the request to the clinic')
  if (state === 'ANSWERED' || state === 'TRANSFERRED') {
    completed.push('Put the call through to the front desk')
  }
  if (state === 'RETURNED') completed.push('Returned the caller to Ava')
  if (callback === 'SAVED') completed.push('Saved a callback request')
  if (intent === 'MESSAGE_LEFT' || secondary.includes('MESSAGE_LEFT')) {
    completed.push('Saved a message for the clinic')
  }

  if (handoff && state) {
    outcomes.push(stateCopy(handoff, atMs, nowMs).description)
  }
  if (information === 'NOT_AVAILABLE') {
    outcomes.push('Flio has no approved answer to this question.')
  }
  if (intent === 'APPOINTMENT_REQUEST') {
    outcomes.push('Flio has no verified booking from this conversation.')
  }
  if (callback === 'DECLINED') {
    outcomes.push('No callback was requested: the caller didn’t take it up.')
  } else if (callback === 'NOT_CONFIRMED') {
    outcomes.push('No callback was requested: the caller didn’t confirm one.')
  } else if (callback === 'NOT_SAVED' || callback === 'PENDING') {
    outcomes.push(CALLBACK_NOTES[callback])
  }
  return { attempted, completed, outcomes }
}

export type TransferEventCopy = {
  heading: string
  outcome: string
  tone: HandoffTone
  icon: LucideIcon
}

/** The transfer's own card in a call's timeline. */
export function transferEventCopy(
  handoff: HandoffSummary,
  atMs: number,
  nowMs: number = Date.now()
): TransferEventCopy | null {
  if (handoff.state === undefined) return null
  const copy = stateCopy(handoff, atMs, nowMs)
  switch (handoff.state) {
    case 'ANSWERED':
      return {
        heading: 'Transferred to Front Desk',
        outcome: 'Front desk answered the call.',
        tone: 'success',
        icon: CircleCheck,
      }
    case 'TRANSFERRED':
      return {
        heading: 'Transferred to Front Desk',
        outcome: 'Staff pickup wasn’t verified.',
        tone: copy.tone,
        icon: copy.icon,
      }
    case 'RETURNED':
      return {
        heading: 'Front Desk Did Not Answer',
        outcome: 'The call returned to Ava.',
        tone: 'warning',
        icon: PhoneMissed,
      }
    case 'CALLER_LEFT':
      return {
        heading: 'Transfer Interrupted',
        outcome: copy.description,
        tone: copy.tone,
        icon: copy.icon,
      }
    default:
      return {
        heading: copy.label,
        outcome: copy.description,
        tone: copy.tone,
        icon: copy.icon,
      }
  }
}

/** The callback request's own card in a call's timeline. */
export function callbackEventCopy(
  callback: 'SAVED' | 'NOT_SAVED' | 'PENDING'
): TransferEventCopy {
  if (callback === 'SAVED') {
    return {
      heading: 'Callback Requested',
      outcome: 'The clinic has a callback request for this caller.',
      tone: 'success',
      icon: PhoneCall,
    }
  }
  return callback === 'PENDING'
    ? {
        heading: 'Callback Pending',
        outcome: 'Flio is still completing this callback request.',
        tone: 'progress',
        icon: LoaderCircle,
      }
    : {
        heading: 'Callback Not Saved',
        outcome: CALLBACK_NOTES.NOT_SAVED,
        tone: 'danger',
        icon: CircleAlert,
      }
}

/** One owner event, to read in place among the words. Null for those no one needs to see. */
export function ownerEventCopy(
  event: CallEvent
): { text: string; tone: HandoffTone } | null {
  switch (event.kind) {
    case 'FRONT_DESK':
      if (event.result !== 'UNAVAILABLE') return null
      return {
        text:
          UNAVAILABLE_REASONS[
            event.reason as NonNullable<HandoffSummary['reason']>
          ] ?? 'The front desk couldn’t be reached.',
        tone: 'warning',
      }
    case 'INFORMATION': {
      const topic = event.topic ? TOPIC_LABELS[event.topic] : undefined
      return event.available
        ? {
            text: `Ava answered from clinic records${topic ? `: ${topic}` : ''}.`,
            tone: 'success',
          }
        : {
            text: `Flio has no approved answer${topic ? `: ${topic}` : ''}.`,
            tone: 'warning',
          }
    }
    case 'CALLBACK':
      if (event.result === 'ASKED') {
        return {
          text: 'Ava asked the caller to confirm a callback. They didn’t confirm one.',
          tone: 'neutral',
        }
      }
      return event.result === 'DECLINED'
        ? {
            text: 'A callback was offered. The caller didn’t take it up.',
            tone: 'neutral',
          }
        : null // a saved or failed callback has its own card
    case 'MESSAGE':
      return event.result === 'SAVED'
        ? { text: 'A message was saved for the clinic.', tone: 'success' }
        : { text: 'A message couldn’t be saved.', tone: 'warning' }
    default:
      return event.result === 'PASSED'
        ? { text: 'The request was passed to the clinic.', tone: 'success' }
        : null
  }
}

// native_voice_call_ledger.TOPICS. OTHER says nothing, and a new one is left out.
/**
 * What the conversation did to the appointment a request was about: nothing.
 * Voice and Text never change one; staff do, in the PMS. This speaks only for
 * this conversation, never for the PMS.
 */
export function appointmentNote(
  intents: Array<InteractionIntent | null | undefined>,
  where: 'call' | 'conversation' = 'call'
) {
  const cancel = intents.includes('CANCELLATION_REQUEST')
  const reschedule = intents.includes('RESCHEDULE_REQUEST')
  if (cancel && reschedule) return `Not cancelled or changed by this ${where}.`
  if (cancel) return `Not cancelled by this ${where}.`
  return reschedule ? `Not changed by this ${where}.` : null
}

const TOPIC_LABELS: Record<string, string> = {
  HOURS: 'Hours',
  LOCATION: 'Location',
  CONTACT: 'Contact details',
  SERVICE: 'Services',
  INSURANCE: 'Insurance',
  PRICE: 'Pricing',
  PROVIDER: 'Providers',
  OTHER_LOCATION: 'Other locations',
  CLINIC_POLICY: 'Clinic policy',
}

export function topicLabels(topics: string[] | undefined) {
  return (topics ?? []).flatMap((topic) => {
    const label = TOPIC_LABELS[topic]
    return label ? [label] : []
  })
}
